import { StaffGetQueryRouteInput } from '@repo/docs-api';
import {
  StaffDeleteNoteRouteResponse,
  StaffGetNotesQueryRouteResponse,
  StaffGetQueryRouteResponse,
  StaffGetRootRouteResponse,
  StaffPostNewRouteBodyInput,
  StaffPostNewRouteResponse,
  StaffPostNoteNewRouteResponse,
  StaffPutNoteUpdateRouteBodyInput,
  StaffPutNoteUpdateRouteResponse,
  StaffPutUpdateRouteBodyInput,
  StaffPutUpdateRouteResponse,
} from '@repo/types/api';
import { FlattenRoute } from '@repo/types/common';

import { cognitoApi } from '../api';
import { EntityResponse, attachId, attachIds } from '../utils';

import { ListedStaff, ListedStaffNote, Staff } from './types';

// Define a service using a base URL and expected endpoints
export const staffApi = cognitoApi.injectEndpoints({
  endpoints: build => ({
    newStaff: build.mutation<StaffPostNewRouteResponse, { attributes: StaffPostNewRouteBodyInput }>(
      {
        query(arg) {
          const { attributes } = arg;

          return {
            url: 'staff/new',
            body: attributes,
            method: 'POST',
          };
        },
        invalidatesTags: ['Users', 'Staff'],
      }
    ),
    getStaff: build.query<EntityResponse<Staff>, { staffId: string }>({
      query: arg => `staff/${arg.staffId ?? ''}`,
      providesTags: (_result, _error, arg) => [{ type: 'Staff', id: arg?.staffId }],
      transformResponse(response: StaffGetRootRouteResponse) {
        return attachId(response, entry => entry.staffId);
      },
    }),
    getStaffs: build.query<
      EntityResponse<ListedStaff[]>,
      FlattenRoute<StaffGetQueryRouteInput> & { limit?: number | 'all' }
    >({
      query: arg => {
        const { excludeDisabled, search, group, limit } = arg;
        return {
          url: 'staff/query',
          method: 'GET',
          params: {
            excludeDisabled,
            search,
            group,
            limit,
          },
        };
      },
      providesTags: result =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'Staff' as const, id })), 'Staff']
          : ['Staff'],
      transformResponse(response: StaffGetQueryRouteResponse) {
        return attachIds(response, entry => entry.staffId);
      },
    }),
    updateStaff: build.mutation<
      StaffPutUpdateRouteResponse,
      { staffId: string; newAttributes: StaffPutUpdateRouteBodyInput }
    >({
      query(arg) {
        const { staffId, newAttributes } = arg;
        return {
          url: `staff/${staffId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'Staff', id: arg?.staffId }],
    }),
    newStaffNote: build.mutation<
      StaffPostNoteNewRouteResponse,
      {
        staffId: string;
        note: string;
        attributes?: {
          assignee?: string;
          dueDatetime?: string;
        };
      }
    >({
      query(arg) {
        const { staffId, note, attributes } = arg;

        return {
          url: `staff/${staffId}/notes/new`,
          body: {
            note,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['UserNotes'],
    }),
    getStaffNotes: build.query<
      EntityResponse<ListedStaffNote[]>,
      {
        staffId: string;
        active?: boolean;
        limit?: number | 'all';
      }
    >({
      query(arg) {
        const { staffId, active, limit } = arg;

        return {
          url: `staff/${staffId}/notes/query`,
          params: { active, limit },
          method: 'GET',
        };
      },
      providesTags: result =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'UserNotes' as const, id })), 'UserNotes']
          : ['UserNotes'],
      transformResponse(response: StaffGetNotesQueryRouteResponse) {
        return attachIds(response, entry => `${entry.userId}#${entry.creationDatetime}`);
      },
    }),
    updateStaffNote: build.mutation<
      StaffPutNoteUpdateRouteResponse,
      {
        staffId: string;
        creationDatetime: number;
        newAttributes: StaffPutNoteUpdateRouteBodyInput;
      }
    >({
      query(arg) {
        const { staffId, creationDatetime, newAttributes } = arg;

        return {
          url: `staff/${staffId}/notes/${creationDatetime}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Every user note should be invalidated if the due date of the note is updated, as
      // a note without a due date could gain a due date; in which case it should show
      // up in the follow up page
      invalidatesTags: (_result, _error, arg) =>
        arg.newAttributes.dueDatetime
          ? ['UserNotes']
          : [{ type: 'UserNotes', id: `${arg.staffId}#${arg.creationDatetime}` }],
    }),
    deleteStaffNote: build.mutation<
      StaffDeleteNoteRouteResponse,
      {
        staffId: string;
        creationDatetime: number;
      }
    >({
      query(arg) {
        const { staffId, creationDatetime } = arg;

        return {
          url: `staff/${staffId}/notes/${creationDatetime}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'UserNotes', id: `${arg.staffId}#${arg.creationDatetime}` },
      ],
    }),
  }),
});

export const {
  useLazyGetStaffQuery,
  useNewStaffMutation,
  useGetStaffQuery,
  useGetStaffsQuery,
  useUpdateStaffMutation,
  useNewStaffNoteMutation,
  useGetStaffNotesQuery,
  useDeleteStaffNoteMutation,
  useUpdateStaffNoteMutation,
} = staffApi;
